import { api } from '../../lib/api';
import type {
  AfCalendarConfig,
  BookingAvailabilityWindow,
  BookingCalendar,
  BookingCalendarDetail,
  BookingCalendarMember,
  BookingRules,
  CalendarAvailability,
  MemberVerifyResult,
} from '../../types/appointments';

// Booking Calendars Phase 1 — the authed staff API.

export type CalendarStatus = {
  /** False ⇒ the calendar service account has not been set up yet. */
  calendarConfigured: boolean;
  /** The address a client shares their calendar WITH. Null when unconfigured. */
  serviceAccountEmail: string | null;
};

export async function getCalendarStatus(): Promise<CalendarStatus> {
  return api<CalendarStatus>('/api/appointments/status');
}

export async function listBookingCalendars(
  accountId?: string,
  options: { includeArchived?: boolean } = {},
): Promise<BookingCalendar[]> {
  const params = new URLSearchParams();
  if (accountId) params.set('accountId', accountId);
  if (options.includeArchived) params.set('includeArchived', '1');
  const query = params.toString() ? `?${params.toString()}` : '';
  const { calendars } = await api<{ calendars: BookingCalendar[] }>(
    `/api/appointments/calendars${query}`,
  );
  return calendars;
}

export async function getBookingCalendar(id: string): Promise<BookingCalendarDetail> {
  return api<BookingCalendarDetail>(`/api/appointments/calendars/${id}`);
}

export type CreateCalendarArgs = {
  accountId: string;
  name: string;
  timezone: string;
  availability?: BookingAvailabilityWindow[];
  rules?: BookingRules;
  anytimeFitness?: AfCalendarConfig;
};

export async function createBookingCalendar(args: CreateCalendarArgs): Promise<BookingCalendar> {
  return api<BookingCalendar>('/api/appointments/calendars', {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

export type UpdateCalendarArgs = {
  name?: string;
  timezone?: string;
  enabled?: boolean;
  availability?: BookingAvailabilityWindow[];
  rules?: BookingRules;
  anytimeFitness?: AfCalendarConfig | null;
};

export async function updateBookingCalendar(
  id: string,
  args: UpdateCalendarArgs,
): Promise<BookingCalendar> {
  return api<BookingCalendar>(`/api/appointments/calendars/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(args),
  });
}

export async function addCalendarMember(
  calendarId: string,
  args: { displayName: string; googleCalendarId: string },
): Promise<BookingCalendarMember> {
  return api<BookingCalendarMember>(`/api/appointments/calendars/${calendarId}/members`, {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

export async function updateCalendarMember(
  calendarId: string,
  memberId: string,
  args: { displayName?: string; googleCalendarId?: string; enabled?: boolean; priority?: number },
): Promise<BookingCalendarMember> {
  return api<BookingCalendarMember>(
    `/api/appointments/calendars/${calendarId}/members/${memberId}`,
    { method: 'PATCH', body: JSON.stringify(args) },
  );
}

export async function removeCalendarMember(calendarId: string, memberId: string): Promise<void> {
  await api<{ ok: true }>(`/api/appointments/calendars/${calendarId}/members/${memberId}`, {
    method: 'DELETE',
  });
}

/**
 * "Test connection" — a real free/busy read for one member.
 *
 * ⚠️ A POST because it writes the outcome onto the member row, so a
 * never-verified or newly-failing calendar can wear a badge without the page
 * having to re-run the check on every load.
 */
export async function verifyCalendarMember(
  calendarId: string,
  memberId: string,
): Promise<MemberVerifyResult> {
  return api<MemberVerifyResult>(
    `/api/appointments/calendars/${calendarId}/members/${memberId}/verify`,
    { method: 'POST' },
  );
}

export async function getCalendarAvailability(calendarId: string): Promise<CalendarAvailability> {
  return api<CalendarAvailability>(`/api/appointments/calendars/${calendarId}/availability`);
}

export interface AfCheckResult {
  ok: boolean;
  error: string | null;
  staffCount: number;
  slotCount: number;
  nextSlot: { startsAt: string; localStart: string } | null;
  timezone: string | null;
  problems: string[];
}

/** The account's Anytime Fitness lead-route club id, to prefill the club field (null = none). */
export async function getAfDefaults(accountId: string): Promise<{ clubId: string | null }> {
  return api<{ clubId: string | null }>(`/api/appointments/af-defaults?accountId=${encodeURIComponent(accountId)}`);
}

/** "Check": ONE availability GET for the club, summarised. Read only. */
export async function checkAfClub(locationNumber: string, accountId: string | null): Promise<AfCheckResult> {
  return api<AfCheckResult>('/api/appointments/af-check', {
    method: 'POST',
    body: JSON.stringify({ locationNumber, ...(accountId ? { accountId } : {}) }),
  });
}

export type ArchiveCheck = {
  bots: Array<{ botConfigId: string; botName: string }>;
  futureAppointments: number;
  archived: boolean;
};

export async function getCalendarArchiveCheck(id: string): Promise<ArchiveCheck> {
  return api<ArchiveCheck>(`/api/appointments/calendars/${id}/archive-check`);
}

export async function archiveBookingCalendar(id: string): Promise<BookingCalendar> {
  return api<BookingCalendar>(`/api/appointments/calendars/${id}/archive`, { method: 'POST' });
}
